import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Trash2 } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import './AppointmentModal.css';

// `datetime-local` expects local wall-clock time. Using toISOString() here would
// shift the value by the UTC offset and show the wrong hour when editing.
const toLocalInputValue = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const AppointmentModal = ({
  isOpen,
  onClose,
  appointment,
  appointmentTypes,
  workers,
  defaultDate,
  defaultCustomer,
  onSave,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isEditing = !!appointment;
  
  // Form state
  const [formData, setFormData] = useState({
    appointment_type_id: '',
    worker_id: '',
    start_time: '',
    name: '',
    phone_number: '',
    notes: '',
    status: 'scheduled',
    send_notification: true,
  });
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (appointment) {
      setFormData({
        appointment_type_id: appointment.appointment_type_id || '',
        worker_id: appointment.worker_id || '',
        start_time: toLocalInputValue(appointment.start_time),
        name: appointment.name || '',
        phone_number: appointment.phone_number || '',
        notes: appointment.notes || '',
        status: appointment.status || 'scheduled',
        send_notification: true,
      });
      return;
    }

    // Creating: always start from a clean slate so values from a previously
    // edited appointment can't leak into the new one.
    setFormData({
      appointment_type_id: '',
      worker_id: '',
      // `defaultDate` is either a plain date (month view) or a full local
      // datetime produced by dragging a range on the calendar.
      start_time: defaultDate
        ? defaultDate.includes('T')
          ? defaultDate.slice(0, 16)
          : `${defaultDate}T09:00`
        : '',
      name: defaultCustomer?.name || '',
      phone_number: defaultCustomer?.phone || '',
      notes: '',
      status: 'scheduled',
      send_notification: true,
    });
  }, [appointment, defaultDate, defaultCustomer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Validation with user feedback
    if (!formData.appointment_type_id) {
      toast.error(t('appointments.typeRequired'));
      return;
    }
    
    if (!formData.start_time) {
      toast.error(t('appointments.startTimeRequired'));
      return;
    }
    
    setLoading(true);
    
    try {
      await onSave({
        appointment_type_id: parseInt(formData.appointment_type_id),
        worker_id: formData.worker_id ? parseInt(formData.worker_id) : null,
        start_time: formData.start_time,
        name: formData.name,
        phone_number: formData.phone_number,
        notes: formData.notes,
        status: formData.status,
        send_notification: formData.send_notification,
      });
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    setLoading(true);
    try {
      await onDelete();
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Status options
  const statusOptions = [
    { value: 'scheduled', label: t('appointments.scheduled') },
    { value: 'completed', label: t('appointments.completed') },
    { value: 'cancelled', label: t('appointments.cancelled') },
  ];

  // Convert types and workers to select options
  const typeOptions = appointmentTypes.map((type) => ({
    value: type.id.toString(),
    label: `${type.name} (${type.duration_minutes} ${t('common.minutes')})`,
  }));

  const workerOptions = [
    { value: '', label: t('appointments.selectWorker') },
    ...workers.filter(w => w.is_active).map((worker) => ({
      value: worker.id.toString(),
      label: worker.name,
    })),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('appointments.editAppointment') : t('appointments.newAppointment')}
      size="lg"
      footer={
        <div className="modal-actions">
          {isEditing && onDelete && (
            <Button
              variant="danger"
              icon={<Trash2 size={16} />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading}
            >
              {t('common.delete')}
            </Button>
          )}
          <div className="modal-actions-right">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
            >
              {isEditing ? t('common.update') : t('common.create')}
            </Button>
          </div>
        </div>
      }
    >
      {showDeleteConfirm ? (
        <div className="delete-confirm">
          <p>{t('appointments.confirmDelete')}</p>
          <div className="delete-confirm-actions">
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={loading}>
              {t('common.delete')}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="appointment-form">
          <div className="form-row">
            <Select
              label={t('appointments.appointmentType')}
              name="appointment_type_id"
              value={formData.appointment_type_id}
              onChange={handleChange}
              options={typeOptions}
              placeholder={t('appointments.selectType')}
              required
            />
            
            <Select
              label={t('appointments.worker')}
              name="worker_id"
              value={formData.worker_id}
              onChange={handleChange}
              options={workerOptions}
            />
          </div>
          
          <div className="form-row">
            <Input
              label={t('appointments.startTime')}
              type="datetime-local"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              required
            />
            
            {isEditing && (
              <Select
                label={t('common.status')}
                name="status"
                value={formData.status}
                onChange={handleChange}
                options={statusOptions}
              />
            )}
          </div>
          
          <div className="form-row">
            <Input
              label={t('appointments.customer')}
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('common.name')}
            />
            
            <Input
              label={t('common.phone')}
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="+972..."
            />
          </div>
          
          <div className="form-row full-width">
            <div className="input-group">
              <label className="input-label">{t('common.notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder={t('appointments.addNotes')}
                className="input textarea"
                rows={3}
              />
            </div>
          </div>
          
          {formData.phone_number && (
            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="send_notification"
                  checked={formData.send_notification}
                  onChange={handleCheckboxChange}
                  className="checkbox-input"
                />
                <span className="checkbox-text">{t('appointments.sendNotification')}</span>
              </label>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
};

export default AppointmentModal;

